/**
 * 外部 API 消費量の設定ページ
 *
 * Google の無料枠は Cloud プロジェクト単位なので、ここに出るのはシステム全体の合計。
 * 枠が尽きると場所検索などが止まるため、月内にどれだけ使ったかを確認できるようにする。
 */

import { useCallback, useEffect, useState } from 'react'

import { getApiUsage } from '@/api/services/api-usage'
import type { ApiUsageItem, ApiUsageResponse } from '@/api/types/api-usage'
import { Header } from '@/components/shared/header'
import { useGoBack } from '@/hooks/useGoBack'
import { parseUtcDate } from '@/lib/date-utils'
import { cn } from '@/lib/utils'

/** 残り件数の色分け（8割超で警告、使い切りで危険） */
const barColor = (ratio: number, warnRatio: number): string => {
  if (ratio >= 1) return 'bg-destructive'
  if (ratio >= warnRatio) return 'bg-amber-500'
  return 'bg-primary'
}

const UsageRow = ({ item, warnRatio }: { item: ApiUsageItem; warnRatio: number }) => {
  const ratio = item.quota > 0 ? item.used / item.quota : 0
  const percent = Math.min(ratio * 100, 100)

  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="flex items-baseline justify-between gap-2">
        <p className="font-medium">
          {item.label}
          {item.is_estimated && <span className="ml-1 text-xs text-muted-foreground">※推定</span>}
        </p>
        <p className="shrink-0 text-sm tabular-nums text-muted-foreground">
          {item.used.toLocaleString()} / {item.quota.toLocaleString()}
        </p>
      </div>
      <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-primary/15">
        <div className={cn('h-full transition-all', barColor(ratio, warnRatio))} style={{ width: `${percent}%` }} />
      </div>
      <p className="mt-1 text-xs text-muted-foreground">{item.sku}</p>
    </div>
  )
}

export const SettingsApiUsagePage = () => {
  const goBack = useGoBack()
  const [usage, setUsage] = useState<ApiUsageResponse | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setUsage(await getApiUsage())
      setError(null)
    } catch {
      setError('消費量を取得できませんでした')
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  return (
    <div className="flex h-dvh flex-col bg-background">
      <Header title="API 消費量" onClose={goBack} />
      <div className="flex-1 space-y-3 overflow-y-auto p-4">
        {error && <p className="text-sm text-destructive">{error}</p>}

        {usage && (
          <>
            <p className="text-xs text-muted-foreground">
              {usage.year_month} の利用状況（{parseUtcDate(usage.resets_at).toLocaleString('ja-JP')} にリセット）
            </p>
            {usage.items.map((item) => (
              <UsageRow key={item.sku} item={item} warnRatio={usage.warn_ratio} />
            ))}
            <p className="pt-2 text-xs text-muted-foreground">
              ※推定と付いた項目はブラウザ側で数えているため、実際の課金件数とは差が出ることがあります。
            </p>
          </>
        )}
      </div>
    </div>
  )
}
